import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { usePendingCurationQuery } from '@/lib/api/promote-back';
import { PromoteBackQueue, type QueueFilter } from '@/components/settings/promote-back-queue';
import { cn } from '@/lib/utils/cn';

/**
 * Settings → Promote-back queue (T-0111 / E01-F08, C20-121).
 *
 * Replaces the T-0096 placeholder. Lists pending curation items
 * proposed back from engagements; firm admins approve (publishes a new
 * template version atomically via T-0029) or reject with a required
 * note. Mirrors `design_handoff_caliber_v2/prototype/screens-firm.js →
 * firmQueue()` and README §4 row "queue".
 *
 * Filters in the header switch between All / Pending / Decided. The
 * "Source engagement" filter is a free-text uuid-prefix match — v1
 * keeps it simple because the queue is firm-scoped and the per-engagement
 * dimension is provenance, not a primary index.
 *
 * Authority gate is firm_admin / curation-permitted partner (ADR-011);
 * 403 responses surface inline next to the action buttons (handled by
 * `<PromoteBackQueue>`).
 *
 * Per the ticket "Risks and unknowns": the queue is a low-traffic
 * firm-admin-only surface; no real-time refresh is wired.
 */
export const Route = createFileRoute('/settings/queue')({
  component: PromoteBackQueueScreen,
});

const FILTERS: Array<{ id: QueueFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'decided', label: 'Decided' },
];

function PromoteBackQueueScreen() {
  const [filter, setFilter] = useState<QueueFilter>('pending');
  const [engagementFilter, setEngagementFilter] = useState('');

  // Always fetch with `include_decided=1` for the subtitle counter so the
  // user sees totals across statuses regardless of which filter is active.
  const countersQuery = usePendingCurationQuery({ includeDecided: true });
  const counters = useMemo(() => {
    const items = countersQuery.data?.items ?? [];
    return {
      pending: items.filter((it) => it.status === 'pending').length,
      approved: items.filter((it) => it.status === 'approved').length,
      rejected: items.filter((it) => it.status === 'rejected').length,
    };
  }, [countersQuery.data]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">Promote-back queue</h1>
          <p className="text-meta text-ink-500">
            {counters.pending} awaiting review · {counters.approved} approved · {counters.rejected}{' '}
            rejected
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Queue filter">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              onClick={() => setFilter(f.id)}
              className={cn(
                'inline-flex h-9 items-center rounded-md px-3 text-body-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                filter === f.id
                  ? 'border border-ink-100 bg-paper-0 text-accent-amber-700'
                  : 'text-ink-700 hover:bg-paper-100 hover:text-ink-900',
              )}
            >
              {f.label}
            </button>
          ))}
          <label className="inline-flex h-9 items-center gap-2 rounded border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-700 focus-within:ring-2 focus-within:ring-accent-amber-600">
            <span className="text-meta text-ink-500">Source eng</span>
            <input
              type="search"
              value={engagementFilter}
              onChange={(e) => setEngagementFilter(e.target.value.trim())}
              placeholder="uuid prefix"
              className="h-full w-32 bg-transparent font-mono text-meta text-ink-700 placeholder:text-ink-300 focus:outline-none"
              aria-label="Filter by source engagement uuid"
            />
          </label>
        </div>
      </div>

      <PromoteBackQueue
        filter={filter}
        sourceEngagementFilter={
          engagementFilter.length > 0
            ? matchEngagement(engagementFilter, countersQuery.data?.items)
            : undefined
        }
      />
    </div>
  );
}

/**
 * Given a uuid-prefix search and the current item list, return the FIRST
 * matching engagement_id (a full uuid) so the child component can do an
 * exact-equality filter. If no item matches, return the raw input so the
 * filter ends up matching zero rows (consistent with what the user sees).
 */
function matchEngagement(
  prefix: string,
  items: ReadonlyArray<{ source_engagement_id: string }> | undefined,
): string {
  if (!items) return prefix;
  const normalized = prefix.toLowerCase();
  const hit = items.find((it) => it.source_engagement_id.toLowerCase().startsWith(normalized));
  return hit ? hit.source_engagement_id : prefix;
}
